import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Confetti from '../components/Confetti';
import TaskComments from '../components/TaskComments';
import TaskTags from '../components/TaskTags';
import ActivityLog from '../components/ActivityLog';
import api from '../services/api';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiEdit2, FiCalendar, FiUser, FiUsers } from 'react-icons/fi';
import { SiGooglecalendar } from 'react-icons/si';
import { format } from 'date-fns';

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);

  useEffect(() => {
    fetchTask();
  }, [id]);

  const fetchTask = async () => {
    try {
      const response = await api.get(`/tasks/${id}`);
      setTask(response.data.task);
      setStatus(response.data.task.status);
    } catch (error) {
      console.error('Error fetching task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await api.patch(`/tasks/${id}/status`, { status: newStatus });
      setTask(response.data.task);
      setStatus(newStatus);
      
      // Show success animation with status change
      const statusLabels = {
        pending: 'Pending',
        in_progress: 'In Progress',
        completed: 'Completed',
        blocked: 'Blocked'
      };
      
      // Show confetti for completed tasks
      if (newStatus === 'completed') {
        setShowConfetti(true);
      }
      
      showSuccess(
        `Task status updated to "${statusLabels[newStatus]}"! ✨`,
        'Status Updated!'
      );
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error updating status';
      showError(errorMsg, 'Failed to Update Status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-500',
      blocked: 'bg-red-500',
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-500',
      medium: 'bg-yellow-500',
      high: 'bg-red-500',
    };
    return colors[priority] || colors.medium;
  };

  const canUpdateStatus = isAdmin || (task && (
    task.assigned_to === user?.id || 
    (task.assignees && Array.isArray(task.assignees) && task.assignees.some(a => a.id === user?.id))
  ));

  const handleAddToCalendar = async () => {
    setCalendarLoading(true);
    try {
      const response = await api.get(`/tasks/${id}/calendar-url`);
      window.open(response.data.calendarUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showError(error.response?.data?.error || 'Could not open Google Calendar', 'Calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-12">
        <p className="text-white/80">Task not found</p>
      </div>
    );
  }

  return (
    <>
      <Confetti show={showConfetti} onComplete={() => setShowConfetti(false)} />
      <div className="space-y-6 animate-fade-in max-w-4xl">
        <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/tasks')}
        className="flex items-center space-x-2 text-white/80 active:text-white transition-colors mb-4 touch-manipulation p-2 -ml-2"
      >
        <FiArrowLeft size={20} />
        <span className="text-sm lg:text-base">Back to Tasks</span>
      </motion.button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-4 lg:p-6 border border-white/20"
      >
        <div className="flex items-start justify-between mb-3 lg:mb-4">
          <div className="flex-1 min-w-0 pr-2">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-2xl lg:text-3xl font-bold text-white break-words">{task.title}</h1>
              {task.recurrence_parent_id && (
                <span className="text-xs px-2 py-1 rounded-full bg-indigo-500/30 text-indigo-200 shrink-0">Monthly</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span
                className={`${getStatusColor(task.status)} text-white text-sm px-3 py-1 rounded-full capitalize`}
              >
                {task.status.replace('_', ' ')}
              </span>
              {task.priority && (
                <span
                  className={`${getPriorityColor(task.priority)} text-white text-sm px-3 py-1 rounded-full capitalize`}
                >
                  {task.priority} priority
                </span>
              )}
            </div>
          </div>
        </div>

        {task.description && (
          <div className="mb-6">
            <h2 className="text-white font-semibold mb-2">Description</h2>
            <p className="text-white/80 leading-relaxed">{task.description}</p>
          </div>
        )}

        {/* Task Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4 mb-4 lg:mb-6">
          {task.team_name && (
            <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg">
              <FiUsers className="text-white/60" size={20} />
              <div>
                <p className="text-white/60 text-xs">Team</p>
                <p className="text-white font-medium">{task.team_name}</p>
              </div>
            </div>
          )}
          {(task.assignees && Array.isArray(task.assignees) && task.assignees.length > 0) || task.assigned_to_name ? (
            <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg">
              <FiUser className="text-white/60" size={20} />
              <div className="flex-1">
                <p className="text-white/60 text-xs mb-1">Assigned To</p>
                {task.assignees && Array.isArray(task.assignees) && task.assignees.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {task.assignees.map((assignee) => (
                      <span
                        key={assignee.id}
                        className="inline-block px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-200"
                      >
                        {assignee.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-white font-medium">{task.assigned_to_name}</p>
                )}
              </div>
            </div>
          ) : null}
          {task.due_date && (
            <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg">
              <FiCalendar className="text-white/60" size={20} />
              <div>
                <p className="text-white/60 text-xs">Due Date</p>
                <p className="text-white font-medium">
                  {format(new Date(task.due_date), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>
          )}
          {task.assigned_by_name && (
            <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg">
              <FiUser className="text-white/60" size={20} />
              <div>
                <p className="text-white/60 text-xs">Assigned By</p>
                <p className="text-white font-medium">{task.assigned_by_name}</p>
              </div>
            </div>
          )}
          {task.assigned_to_name && task.assigned_by_name && task.assigned_to_name !== task.assigned_by_name && (
            <div className="mt-2 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <p className="text-blue-200 text-xs">
                <span className="font-semibold">{task.assigned_by_name}</span> assigned this task to <span className="font-semibold">{task.assigned_to_name}</span>
              </p>
            </div>
          )}
        </div>

        {/* Add to Google Calendar */}
        <div className="mb-4 lg:mb-6">
          <button
            type="button"
            onClick={handleAddToCalendar}
            disabled={calendarLoading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all bg-[#4285f4] hover:bg-[#3367d6] text-white shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <SiGooglecalendar size={20} />
            {calendarLoading ? 'Opening…' : 'Add to Google Calendar'}
          </button>
        </div>

        {/* Tags */}
        <TaskTags taskId={task.id} tags={task.tags || []} />

        {/* Status Update */}
        {canUpdateStatus && (
          <div className="pt-4 lg:pt-6 border-t border-white/10">
            <h3 className="text-white font-semibold mb-3 text-base lg:text-lg">Update Status</h3>
            <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2">
              {['pending', 'in_progress', 'completed', 'blocked'].map((statusOption) => (
                <button
                  key={statusOption}
                  onClick={() => handleStatusUpdate(statusOption)}
                  disabled={updating || status === statusOption}
                  className={`px-4 py-3 rounded-xl font-medium transition-all capitalize touch-manipulation text-sm lg:text-base ${
                    status === statusOption
                      ? 'bg-white text-primary-600 shadow-lg'
                      : 'bg-white/10 text-white/80 active:bg-white/20'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {statusOption.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <TaskComments taskId={task.id} />

        {/* Activity Log */}
        <ActivityLog taskId={task.id} />
      </motion.div>
      </div>
    </>
  );
};

export default TaskDetail;
